import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Save,
  X,
  FileJson,
  List,
  Grid,
  Settings,
  AlertCircle,
  Check,
  Copy,
  Download,
  Upload,
  RefreshCw,
  Code,
  Table,
  Columns,
  Type,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Search,
  Filter,
  Layout,
  Shield,
  EyeOff,
  Eye as EyeIcon
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const EntityConfigManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showFieldNameModal, setShowFieldNameModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // Super Admin - NO tenant context, only global configs
  const tenantCode = null;

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/entity-config', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch entity configurations');
      }

      const data = await response.json();
      const baseEntities = data.entities || [];

      const enrichedEntities = await Promise.all(
        baseEntities.map(async (entity) => {
          const metadata = { ...(entity.metadata || {}) };

          if (!metadata.displayNameTH || !metadata.displayNamePluralTH) {
            try {
              const detailResponse = await fetch(`/api/entity-config/${entity.entityName}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });

              if (detailResponse.ok) {
                const detailData = await detailResponse.json();
                const config = detailData.entity || detailData;
                const configMeta = config?.entity || {};

                metadata.displayNameTH = metadata.displayNameTH || configMeta.displayNameTH || metadata.displayName;
                metadata.displayNamePluralTH = metadata.displayNamePluralTH || configMeta.displayNamePluralTH || metadata.displayNamePlural;
              }
            } catch (detailError) {
              console.warn(`Failed to enrich entity metadata for ${entity.entityName}`, detailError);
            }
          }

          return {
            ...entity,
            metadata
          };
        })
      );

      setEntities(enrichedEntities);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntity = () => {
    setShowCreateModal(true);
  };

  const handleEditEntity = async (entityName) => {
    try {
      const response = await fetch(`/api/entity-config/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch entity details');
      }

      const data = await response.json();
      setSelectedEntity(data.entity);
      setShowEditModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditFieldNames = async (entityName) => {
    try {
      const token = localStorage.getItem('token') || '';
      const authHeaders = {
        'Authorization': `Bearer ${token}`
      };

      // Super Admin: Load ONLY global configuration (no tenant overrides)
      const globalResponse = await fetch(`/api/entity-config/${entityName}`, {
        headers: authHeaders
      });
      
      if (!globalResponse.ok) {
        throw new Error('Failed to fetch global entity configuration');
      }

      const globalData = await globalResponse.json();
      const globalConfig = globalData.entity || globalData.config || globalData;

      setSelectedEntity({
        entityName,
        config: globalConfig,
        overrides: {}, // No tenant overrides for super admin
        globalConfig: globalConfig,
        tenantCode: null // Super admin has no tenant context
      });
      setShowFieldNameModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteEntity = async (entityName) => {
    if (!window.confirm(`Are you sure you want to delete the entity configuration for "${entityName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/entity-config/${entityName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete entity configuration');
      }

      await fetchEntities();
      alert('Entity configuration deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportEntity = async (entityName) => {
    try {
      const response = await fetch(`/api/entity-config/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch entity configuration');
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data.entity, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ent_${entityName.toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleValidateEntity = async (entityName) => {
    try {
      const response = await fetch(`/api/tenant-entity-config/validate/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to validate entity configuration');
      }

      const data = await response.json();
      setValidationResults(data.validation);
      setSelectedEntity({ entityName });
      setShowValidationModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleValidateAll = async () => {
    try {
      setError(null);
      const response = await fetch('/api/tenant-entity-config/validate-all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to validate entities');
      }

      const data = await response.json();
      
      if (data.totalEntitiesWithIssues === 0) {
        alert('✅ All entities validated successfully! No issues found.');
      } else {
        alert(`⚠️ Found ${data.totalEntitiesWithIssues} ${data.totalEntitiesWithIssues === 1 ? 'entity' : 'entities'} with validation issues.`);
        // Display the first entity with issues
        if (data.validations && data.validations.length > 0) {
          setValidationResults(data.validations[0]);
          setSelectedEntity({ entityName: data.validations[0].entityName });
          setShowValidationModal(true);
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Database className="h-7 w-7" />
            <span>Entity Configuration Manager</span>
            <span className="ml-3 px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
              Super Admin - Global Config Only
            </span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage global entity configurations • Changes affect all tenants
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
              title="List View"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
              title="Grid View"
            >
              <Grid className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={handleValidateAll}
            className="bg-purple-600 dark:bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 flex items-center space-x-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            <span>Validate All</span>
          </button>
          <button
            onClick={fetchEntities}
            className="bg-gray-600 dark:bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleCreateEntity}
            className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Entity</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Entities</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {entities.length}
              </p>
            </div>
            <Database className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Fields</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {entities.reduce((sum, e) => sum + e.fieldCount, 0)}
              </p>
            </div>
            <Settings className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Relationships</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {entities.reduce((sum, e) => sum + e.relationshipCount, 0)}
              </p>
            </div>
            <Code className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Fields/Entity</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {entities.length > 0 ? Math.round(entities.reduce((sum, e) => sum + e.fieldCount, 0) / entities.length) : 0}
              </p>
            </div>
            <FileJson className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
      </div>

      {/* Entity List/Grid */}
      {viewMode === 'list' ? (
        <EntityListView
          entities={entities}
          onEdit={handleEditEntity}
          onDelete={handleDeleteEntity}
          onExport={handleExportEntity}
          onEditFieldNames={handleEditFieldNames}
          onValidate={handleValidateEntity}
        />
      ) : (
        <EntityGridView
          entities={entities}
          onEdit={handleEditEntity}
          onDelete={handleDeleteEntity}
          onExport={handleExportEntity}
          onEditFieldNames={handleEditFieldNames}
          onValidate={handleValidateEntity}
        />
      )}

      {/* Create Entity Modal */}
      {showCreateModal && (
        <CreateEntityModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchEntities();
          }}
        />
      )}

      {/* Edit Entity Modal */}
      {showEditModal && selectedEntity && (
        <EditEntityModal
          entity={selectedEntity}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEntity(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedEntity(null);
            fetchEntities();
          }}
          onAddField={() => {
            setShowEditModal(false);
            setShowFieldModal(true);
          }}
        />
      )}

      {/* Add Field Modal */}
      {showFieldModal && selectedEntity && (
        <AddFieldModal
          entity={selectedEntity}
          onClose={() => {
            setShowFieldModal(false);
            // Keep selectedEntity so user can go back to edit modal
          }}
          onSuccess={async () => {
            const entityName = selectedEntity.entity.name;
            setShowFieldModal(false);
            await fetchEntities();
            // Reload the entity to get updated version
            try {
              const response = await fetch(`/api/entity-config/${entityName}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              if (response.ok) {
                const data = await response.json();
                const updatedEntity = data.entity;
                setSelectedEntity(updatedEntity);
                // Optionally reopen edit modal
                const continueEditing = window.confirm('Field added successfully! Do you want to continue editing the entity?');
                if (continueEditing) {
                  setShowEditModal(true);
                }
              }
            } catch (err) {
              console.error('Failed to reload entity:', err);
            }
          }}
        />
      )}

      {/* Edit Field Names Modal */}
      {showFieldNameModal && selectedEntity && (
        <FieldNameModal
          entity={selectedEntity}
          onClose={() => {
            setShowFieldNameModal(false);
            setSelectedEntity(null);
          }}
          onSuccess={() => {
            setShowFieldNameModal(false);
            setSelectedEntity(null);
            fetchEntities();
          }}
        />
      )}

      {/* Validation Modal */}
      {showValidationModal && validationResults && selectedEntity && (
        <ValidationModal
          entityName={selectedEntity.entityName}
          validation={validationResults}
          onClose={() => {
            setShowValidationModal(false);
            setValidationResults(null);
            setSelectedEntity(null);
          }}
          onFieldsRemoved={() => {
            setShowValidationModal(false);
            setValidationResults(null);
            setSelectedEntity(null);
            fetchEntities();
          }}
        />
      )}
    </div>
  );
};

// Entity List View Component
const EntityListView = ({ entities, onEdit, onDelete, onExport, onEditFieldNames, onValidate }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Entity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Display Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Fields
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Relationships
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Version
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Last Updated
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {entities.map((entity) => (
            <tr key={entity.entityName} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {entity.metadata.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {entity.fileName}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 dark:text-white">
                  {entity.metadata.displayName}
                  {entity.metadata.displayNameTH && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {entity.metadata.displayNameTH}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {entity.metadata.displayNamePlural}
                  {entity.metadata.displayNamePluralTH && (
                    <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                      {entity.metadata.displayNamePluralTH}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  {entity.fieldCount} fields
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                  {entity.relationshipCount} relations
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {entity.metadata.version}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {new Date(entity.lastModified).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => onValidate && onValidate(entity.entityName)}
                    className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300"
                    title="Validate Against Schema"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onExport(entity.entityName)}
                    className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                    title="Export"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEditFieldNames && onEditFieldNames(entity.entityName)}
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300"
                    title="Edit Field Names"
                  >
                    <Type className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(entity.entityName)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(entity.entityName)}
                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {entities.length === 0 && (
        <div className="text-center py-12">
          <Database className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">No entity configurations found</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create your first entity to get started
          </p>
        </div>
      )}
    </div>
  );
};

// Entity Grid View Component
const EntityGridView = ({ entities, onEdit, onDelete, onExport, onEditFieldNames, onValidate }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {entities.map((entity) => (
        <div
          key={entity.entityName}
          className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
        >
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                  <Database className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {entity.metadata.displayName}
                    {entity.metadata.displayNameTH && (
                      <span className="block text-sm font-normal text-gray-500 dark:text-gray-400">
                        {entity.metadata.displayNameTH}
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {entity.metadata.name}
                  </p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300">
                v{entity.metadata.version}
              </span>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
              {entity.metadata.description}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">Fields</p>
                <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{entity.fieldCount}</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">Relations</p>
                <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{entity.relationshipCount}</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
              <span>Updated: {new Date(entity.lastModified).toLocaleDateString()}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={() => onValidate && onValidate(entity.entityName)}
                className="bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-3 py-2 rounded hover:bg-orange-100 dark:hover:bg-orange-900/40 flex items-center justify-center space-x-1"
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Validate</span>
              </button>
              <button
                onClick={() => onExport(entity.entityName)}
                className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-3 py-2 rounded hover:bg-green-100 dark:hover:bg-green-900/40 flex items-center justify-center space-x-1"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onEditFieldNames && onEditFieldNames(entity.entityName)}
                className="flex-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-3 py-2 rounded hover:bg-purple-100 dark:hover:bg-purple-900/40 flex items-center justify-center space-x-1"
              >
                <Type className="h-4 w-4" />
                <span>Field Names</span>
              </button>
              <button
                onClick={() => onEdit(entity.entityName)}
                className="flex-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-3 py-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center justify-center space-x-1"
              >
                <Edit className="h-4 w-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={() => onDelete(entity.entityName)}
                className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded hover:bg-red-100 dark:hover:bg-red-900/40"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {entities.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Database className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">No entity configurations found</p>
        </div>
      )}
    </div>
  );
};

// Create Entity Modal Component (to be continued in next file)
const CreateEntityModal = ({ onClose, onSuccess }) => {
  const [entityName, setEntityName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNamePlural, setDisplayNamePlural] = useState('');
  const [displayNameTH, setDisplayNameTH] = useState('');
  const [displayNamePluralTH, setDisplayNamePluralTH] = useState('');
  const [description, setDescription] = useState('');
  const [primaryTable, setPrimaryTable] = useState('');
  const [primaryKey, setPrimaryKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (displayName && !displayNamePlural) {
      setDisplayNamePlural(`${displayName}s`);
    }
  }, [displayName, displayNamePlural]);

  useEffect(() => {
    if (displayName && !displayNameTH) {
      setDisplayNameTH(displayName);
    }
  }, [displayName, displayNameTH]);

  useEffect(() => {
    if (displayNameTH && !displayNamePluralTH) {
      setDisplayNamePluralTH(displayNameTH);
    }
  }, [displayNameTH, displayNamePluralTH]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Fetch template
      const templateResponse = await fetch('/api/entity-config/template/new', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!templateResponse.ok) {
        throw new Error('Failed to fetch template');
      }

      const { template } = await templateResponse.json();

      const normalizedEntityName = entityName.trim();
      const normalizedDisplayName = displayName.trim();
      const normalizedDisplayNamePlural = (displayNamePlural || '').trim();
      const normalizedDisplayNameTH = (displayNameTH || '').trim();
      const normalizedDisplayNamePluralTH = (displayNamePluralTH || '').trim();
      const normalizedPrimaryKey = (primaryKey || '').trim() || `${normalizedEntityName}ID`;
      const normalizedPrimaryTable = (primaryTable || '').trim() || `${normalizedEntityName}s`;

      const finalDisplayName = normalizedDisplayName || normalizedEntityName;
      const finalDisplayNamePlural = normalizedDisplayNamePlural || `${finalDisplayName}s`;
      const finalDisplayNameTH = normalizedDisplayNameTH || finalDisplayName;
      const finalDisplayNamePluralTH = normalizedDisplayNamePluralTH || finalDisplayNameTH;

      // Customize template
      template.entity.name = normalizedEntityName;
      template.entity.displayName = finalDisplayName;
      template.entity.displayNamePlural = finalDisplayNamePlural;
      template.entity.displayNameTH = finalDisplayNameTH;
      template.entity.displayNamePluralTH = finalDisplayNamePluralTH;
      template.entity.description = description;
      template.entity.primaryKey = normalizedPrimaryKey;
      template.tables.primary = normalizedPrimaryTable;

      // Update primary key field
      const oldPrimaryKey = Object.keys(template.fields)[0];
      const primaryKeyConfig = template.fields[oldPrimaryKey];
      delete template.fields[oldPrimaryKey];
      
      primaryKeyConfig.displayName = `${finalDisplayName} ID`;
      primaryKeyConfig.displayNameTH = `${finalDisplayNameTH}`;
      primaryKeyConfig.database.column = normalizedPrimaryKey;
      template.fields[normalizedPrimaryKey] = primaryKeyConfig;

      // Create entity
      const response = await fetch('/api/entity-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          entityName: normalizedEntityName,
          configuration: template
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create entity');
      }

      alert('Entity created successfully!');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>Create New Entity</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Entity Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., Product, Invoice, Customer"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Internal name (alphanumeric, no spaces)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., Product, Invoice, Customer"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              User-friendly name shown in UI
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Name (Thai)
            </label>
            <input
              type="text"
              value={displayNameTH}
              onChange={(e) => setDisplayNameTH(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="เช่น ลูกค้า"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              ชื่อภาษาไทยสำหรับแสดงในระบบ (เว้นว่างเพื่อใช้ค่าวันนี้)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name (Plural)
              </label>
              <input
                type="text"
                value={displayNamePlural}
                onChange={(e) => setDisplayNamePlural(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Customers"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to auto-generate (append s)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name (Thai, Plural)
              </label>
              <input
                type="text"
                value={displayNamePluralTH}
                onChange={(e) => setDisplayNamePluralTH(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="เช่น ลูกค้า"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="3"
              placeholder="Brief description of what this entity represents"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Table
              </label>
              <input
                type="text"
                value={primaryTable}
                onChange={(e) => setPrimaryTable(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder={`${entityName}s`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to auto-generate
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Key
              </label>
              <input
                type="text"
                value={primaryKey}
                onChange={(e) => setPrimaryKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder={`${entityName}ID`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to auto-generate
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  <span>Create Entity</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Entity Properties Tab Component
const EntityPropertiesTab = ({ entityData, onChange, isEditing, onToggleEdit }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Entity Information</h4>
        <button
          onClick={onToggleEdit}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
        >
          {isEditing ? 'View Mode' : 'Edit Mode'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Entity Name
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.name}
              onChange={(e) => onChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white font-mono">{entityData.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Display Name (English)
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.displayName}
              onChange={(e) => onChange('displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white">{entityData.displayName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Display Name (Thai)
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.displayNameTH}
              onChange={(e) => onChange('displayNameTH', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white">{entityData.displayNameTH}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Display Name Plural (English)
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.displayNamePlural}
              onChange={(e) => onChange('displayNamePlural', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white">{entityData.displayNamePlural}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Display Name Plural (Thai)
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.displayNamePluralTH}
              onChange={(e) => onChange('displayNamePluralTH', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white">{entityData.displayNamePluralTH}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Version
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.version}
              onChange={(e) => onChange('version', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          ) : (
            <p className="text-gray-900 dark:text-white">{entityData.version}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Primary Key
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.primaryKey}
              onChange={(e) => onChange('primaryKey', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            />
          ) : (
            <p className="text-gray-900 dark:text-white font-mono">{entityData.primaryKey}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Primary Table
          </label>
          {isEditing ? (
            <input
              type="text"
              value={entityData.primaryTable}
              onChange={(e) => onChange('primaryTable', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            />
          ) : (
            <p className="text-gray-900 dark:text-white font-mono">{entityData.primaryTable}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        {isEditing ? (
          <textarea
            value={entityData.description}
            onChange={(e) => onChange('description', e.target.value)}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        ) : (
          <p className="text-gray-900 dark:text-white">{entityData.description || 'No description'}</p>
        )}
      </div>
    </div>
  );
};

// Fields Tab Component
const FieldsTab = ({
  fieldsByGroup,
  expandedGroups,
  onToggleGroup,
  onExpandAll,
  onCollapseAll,
  selectedField,
  onSelectField,
  fieldSearch,
  onSearchChange
}) => {
  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={fieldSearch}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search fields by name, display name, or group..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onExpandAll}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Expand All
          </button>
          <button
            onClick={onCollapseAll}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Fields by Group */}
      <div className="space-y-2">
        {Object.entries(fieldsByGroup).map(([groupName, fields]) => {
          const isExpanded = expandedGroups.has(groupName);
          return (
            <div key={groupName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => onToggleGroup(groupName)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                  {isExpanded ? (
                    <FolderOpen className="h-5 w-5 text-blue-500" />
                  ) : (
                    <Folder className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="font-semibold text-gray-900 dark:text-white">{groupName}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                    {fields.length} field{fields.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="p-2 space-y-1 bg-white dark:bg-gray-800">
                  {fields.map((field) => (
                    <button
                      key={field.name}
                      onClick={() => onSelectField(field.name)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedField === field.name
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 dark:border-blue-400'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900 dark:text-white">{field.name}</span>
                            {field.ui?.showInList && (
                              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">List</span>
                            )}
                            {field.ui?.showInForm && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">Form</span>
                            )}
                            {field.ui?.showInDetail && (
                              <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">Detail</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {field.displayName || field.name}
                            {field.displayNameTH && field.displayNameTH !== field.displayName && (
                              <span className="ml-2 text-gray-500">({field.displayNameTH})</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {field.dataType || 'text'} • {field.database?.table || 'N/A'}.{field.database?.column || 'N/A'}
                          </p>
                        </div>
                        <Edit className="h-4 w-4 text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(fieldsByGroup).length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Columns className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No fields found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Visibility Tab Component
const VisibilityTab = ({ entityFields, onFieldChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredFields = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return Object.entries(entityFields).filter(([fieldName, field]) => {
      if (!query) return true;
      return fieldName.toLowerCase().includes(query) ||
        (field.displayName || '').toLowerCase().includes(query) ||
        (field.displayNameTH || '').toLowerCase().includes(query);
    });
  }, [entityFields, searchTerm]);

  const handleToggleVisibility = (fieldName, viewType, currentValue) => {
    onFieldChange(fieldName, `ui.${viewType}`, !currentValue);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Field Visibility Settings</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Control default visibility of fields in lists, forms, and detail views. These settings affect all tenants by default.
          </p>
        </div>
      </div>

      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-orange-900 dark:text-orange-100">Global Settings</h5>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              Changes here affect the default visibility for all tenants. Individual tenants can override these settings using the Field Visibility Manager.
            </p>
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search fields..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Field</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Show in List</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Show in Form</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Show in Detail</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredFields.map(([fieldName, field]) => (
              <tr key={fieldName} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{fieldName}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {field.displayName || fieldName}
                      {field.displayNameTH && field.displayNameTH !== field.displayName && (
                        <span className="ml-1">({field.displayNameTH})</span>
                      )}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.ui?.showInList !== false}
                      onChange={() => handleToggleVisibility(fieldName, 'showInList', field.ui?.showInList !== false)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </td>
                <td className="px-4 py-3 text-center">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.ui?.showInForm !== false}
                      onChange={() => handleToggleVisibility(fieldName, 'showInForm', field.ui?.showInForm !== false)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </td>
                <td className="px-4 py-3 text-center">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.ui?.showInDetail !== false}
                      onChange={() => handleToggleVisibility(fieldName, 'showInDetail', field.ui?.showInDetail !== false)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredFields.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <EyeOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No fields match your search</p>
        </div>
      )}
    </div>
  );
};

// Relationships Tab Component
const RelationshipsTab = ({ relationships }) => {
  const rels = relationships || {};
  return (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Entity Relationships</h4>
      {Object.keys(rels).length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No relationships defined</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(rels).map(([relName, relConfig]) => (
            <div key={relName} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white">{relName}</h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Type: {relConfig.type || 'N/A'} • Target: {relConfig.targetEntity || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// JSON View Tab Component
const JsonViewTab = ({ entity }) => {
  const [jsonValue, setJsonValue] = useState(JSON.stringify(entity, null, 2));
  
  useEffect(() => {
    setJsonValue(JSON.stringify(entity, null, 2));
  }, [entity]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">JSON Configuration</h4>
        <button
          onClick={() => {
            navigator.clipboard.writeText(jsonValue);
            alert('JSON copied to clipboard!');
          }}
          className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg flex items-center space-x-2"
        >
          <Copy className="h-4 w-4" />
          <span>Copy</span>
        </button>
      </div>
      <textarea
        value={jsonValue}
        onChange={(e) => setJsonValue(e.target.value)}
        className="w-full h-[600px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm"
        spellCheck={false}
      />
    </div>
  );
};

// Field Editor Sidebar Component
const FieldEditorSidebar = ({ fieldName, fieldConfig, activeTab, onTabChange, onChange }) => {
  const fieldTabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'validation', label: 'Validation', icon: Shield },
    { id: 'ui', label: 'UI', icon: Layout },
    { id: 'group', label: 'Group', icon: Folder }
  ];

  return (
    <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h4 className="font-semibold text-gray-900 dark:text-white">{fieldName}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {fieldConfig.displayName || fieldName}
        </p>
      </div>

      {/* Field Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex overflow-x-auto">
          {fieldTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center space-x-1 px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="h-3 w-3" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Field Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'general' && (
          <GeneralTab fieldConfig={fieldConfig} fieldName={fieldName} onChange={onChange} />
        )}
        {activeTab === 'database' && (
          <DatabaseTab fieldConfig={fieldConfig} fieldName={fieldName} onChange={onChange} />
        )}
        {activeTab === 'validation' && (
          <ValidationTab fieldConfig={fieldConfig} fieldName={fieldName} onChange={onChange} />
        )}
        {activeTab === 'ui' && (
          <UITab fieldConfig={fieldConfig} fieldName={fieldName} onChange={onChange} />
        )}
        {activeTab === 'group' && (
          <GroupTab fieldConfig={fieldConfig} fieldName={fieldName} onChange={onChange} />
        )}
      </div>
    </div>
  );
};

// General Tab Component
const GeneralTab = ({ fieldConfig, fieldName, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Display Name (English) <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          value={fieldConfig.displayName || ''}
          onChange={(e) => onChange(fieldName, 'displayName', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="e.g., First Name"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          User-friendly name shown in UI (English)
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Display Name (Thai)
        </label>
        <input
          type="text"
          value={fieldConfig.displayNameTH || ''}
          onChange={(e) => onChange(fieldName, 'displayNameTH', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="เช่น ชื่อ"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          ชื่อภาษาไทยสำหรับแสดงใน UI (Thai)
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={fieldConfig.description || ''}
          onChange={(e) => onChange(fieldName, 'description', e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Field description"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Brief description of what this field represents
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Data Type
        </label>
        <select
          value={fieldConfig.dataType || 'text'}
          onChange={(e) => onChange(fieldName, 'dataType', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="text">Text</option>
          <option value="integer">Integer</option>
          <option value="decimal">Decimal</option>
          <option value="boolean">Boolean</option>
          <option value="date">Date</option>
          <option value="datetime">DateTime</option>
          <option value="textarea">Textarea</option>
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          The data type of this field
        </p>
      </div>
    </div>
  );
};

// Database Tab Component
const DatabaseTab = ({ fieldConfig, fieldName, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Table</label>
        <input
          type="text"
          value={fieldConfig.database?.table || ''}
          onChange={(e) => onChange(fieldName, 'database.table', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Column</label>
        <input
          type="text"
          value={fieldConfig.database?.column || ''}
          onChange={(e) => onChange(fieldName, 'database.column', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">SQL Type</label>
        <input
          type="text"
          value={fieldConfig.database?.type || ''}
          onChange={(e) => onChange(fieldName, 'database.type', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          placeholder="e.g., NVARCHAR(100) NOT NULL"
        />
      </div>
    </div>
  );
};

// Validation Tab Component
const ValidationTab = ({ fieldConfig, fieldName, onChange }) => {
  const validation = fieldConfig.validation || {};
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={validation.required || false}
            onChange={(e) => onChange(fieldName, 'validation.required', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
        </label>
      </div>
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={validation.unique || false}
            onChange={(e) => onChange(fieldName, 'validation.unique', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Unique</span>
        </label>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Max Length</label>
        <input
          type="number"
          value={validation.maxLength || ''}
          onChange={(e) => onChange(fieldName, 'validation.maxLength', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pattern (Regex)</label>
        <input
          type="text"
          value={validation.pattern || ''}
          onChange={(e) => onChange(fieldName, 'validation.pattern', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          placeholder="e.g., ^[A-Za-z]+$"
        />
      </div>
    </div>
  );
};

// UI Tab Component
const UITab = ({ fieldConfig, fieldName, onChange }) => {
  const ui = fieldConfig.ui || {};
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Input Type</label>
        <select
          value={ui.inputType || 'text'}
          onChange={(e) => onChange(fieldName, 'ui.inputType', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="email">Email</option>
          <option value="tel">Telephone</option>
          <option value="url">URL</option>
          <option value="textarea">Textarea</option>
          <option value="select">Select</option>
          <option value="checkbox">Checkbox</option>
          <option value="date">Date</option>
          <option value="datetime">DateTime</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Display Order</label>
        <input
          type="number"
          value={ui.order || ''}
          onChange={(e) => onChange(fieldName, 'ui.order', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Placeholder</label>
        <input
          type="text"
          value={ui.placeholder || ''}
          onChange={(e) => onChange(fieldName, 'ui.placeholder', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Visibility</label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={ui.showInList !== false}
            onChange={(e) => onChange(fieldName, 'ui.showInList', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show in List</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={ui.showInForm !== false}
            onChange={(e) => onChange(fieldName, 'ui.showInForm', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show in Form</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={ui.showInDetail !== false}
            onChange={(e) => onChange(fieldName, 'ui.showInDetail', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show in Detail</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={ui.showInExport !== false}
            onChange={(e) => onChange(fieldName, 'ui.showInExport', e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show in Export</span>
        </label>
      </div>
    </div>
  );
};

// Group Tab Component
const GroupTab = ({ fieldConfig, fieldName, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Group Name</label>
        <input
          type="text"
          value={fieldConfig.group || ''}
          onChange={(e) => onChange(fieldName, 'group', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="e.g., Basic Information"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Fields with the same group name will be displayed together
        </p>
      </div>
    </div>
  );
};

// Edit Entity Modal Component with Tabbed Interface
const EditEntityModal = ({ entity, onClose, onSuccess, onAddField }) => {
  const [activeTab, setActiveTab] = useState('entity');
  const [activeFieldTab, setActiveFieldTab] = useState('general');
  const [selectedField, setSelectedField] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [fieldSearch, setFieldSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Entity state
  const [entityData, setEntityData] = useState({
    name: entity?.entity?.name || '',
    displayName: entity?.entity?.displayName || '',
    displayNameTH: entity?.entity?.displayNameTH || '',
    displayNamePlural: entity?.entity?.displayNamePlural || '',
    displayNamePluralTH: entity?.entity?.displayNamePluralTH || '',
    description: entity?.entity?.description || '',
    version: entity?.entity?.version || '1.0.0',
    primaryKey: entity?.entity?.primaryKey || '',
    primaryTable: entity?.tables?.primary || ''
  });

  // Entity state for fields
  const [entityFields, setEntityFields] = useState(entity?.fields || {});

  // Update entity fields when entity changes
  useEffect(() => {
    setEntityFields(entity?.fields || {});
  }, [entity]);

  // Fields organized by groups
  const fieldsByGroup = useMemo(() => {
    if (!entityFields || Object.keys(entityFields).length === 0) return {};
    const groups = {};
    Object.entries(entityFields).forEach(([fieldName, fieldConfig]) => {
      const group = fieldConfig.group || fieldConfig.ui?.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push({ name: fieldName, ...fieldConfig });
    });
    // Sort fields within each group by order
    Object.keys(groups).forEach(group => {
      groups[group].sort((a, b) => (a.ui?.order || 0) - (b.ui?.order || 0));
    });
    return groups;
  }, [entityFields]);

  // Filtered fields
  const filteredFieldsByGroup = useMemo(() => {
    if (!fieldSearch.trim()) return fieldsByGroup;
    const query = fieldSearch.toLowerCase();
    const filtered = {};
    Object.entries(fieldsByGroup).forEach(([group, fields]) => {
      const matchingFields = fields.filter(f => 
        f.name.toLowerCase().includes(query) ||
        (f.displayName || '').toLowerCase().includes(query) ||
        (f.displayNameTH || '').toLowerCase().includes(query) ||
        group.toLowerCase().includes(query)
      );
      if (matchingFields.length > 0) filtered[group] = matchingFields;
    });
    return filtered;
  }, [fieldsByGroup, fieldSearch]);

  // Toggle group expansion
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Expand all groups
  const expandAllGroups = () => {
    setExpandedGroups(new Set(Object.keys(fieldsByGroup)));
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    setExpandedGroups(new Set());
  };

  // Handle entity data change
  const handleEntityChange = (key, value) => {
    setEntityData(prev => ({ ...prev, [key]: value }));
  };

  // Handle field change
  const handleFieldChange = (fieldName, key, value) => {
    if (!entityFields[fieldName]) return;
    
    setEntityFields(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        if (!updated[fieldName][parent]) {
          updated[fieldName][parent] = {};
        }
        updated[fieldName][parent][child] = value;
      } else {
        updated[fieldName][key] = value;
      }
      return updated;
    });
  };

  // Save entity
  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const updatedEntity = JSON.parse(JSON.stringify(entity));
      
      // Update entity metadata
      updatedEntity.entity = {
        ...updatedEntity.entity,
        ...entityData,
        lastUpdated: new Date().toISOString().split('T')[0]
      };
      
      // Update primary table
      if (entityData.primaryTable) {
        updatedEntity.tables = updatedEntity.tables || {};
        updatedEntity.tables.primary = entityData.primaryTable;
      }

      // Update fields from entityFields state
      updatedEntity.fields = entityFields;

      const response = await fetch(`/api/entity-config/${entity.entity.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ configuration: updatedEntity })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update entity');
      }

      alert('Entity updated successfully!');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'entity', label: 'Entity Properties', icon: Settings },
    { id: 'fields', label: 'Fields', icon: Columns },
    { id: 'visibility', label: 'Field Visibility', icon: EyeOff },
    { id: 'relationships', label: 'Relationships', icon: Code },
    { id: 'json', label: 'JSON View', icon: FileJson }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <Edit className="h-5 w-5" />
              <span>Edit Entity: {entity.entity.displayName}</span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {Object.keys(entity.fields || {}).length} fields • {Object.keys(fieldsByGroup).length} groups
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {onAddField && (
              <button
                onClick={onAddField}
                className="bg-green-600 dark:bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center space-x-2 text-sm"
                title="Add New Field"
              >
                <Plus className="h-4 w-4" />
                <span>Add Field</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2 mb-4">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
              </div>
            )}

            {activeTab === 'entity' && (
              <EntityPropertiesTab
                entityData={entityData}
                onChange={handleEntityChange}
                isEditing={isEditing}
                onToggleEdit={() => setIsEditing(!isEditing)}
              />
            )}

            {activeTab === 'fields' && (
              <FieldsTab
                fieldsByGroup={filteredFieldsByGroup}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                onExpandAll={expandAllGroups}
                onCollapseAll={collapseAllGroups}
                selectedField={selectedField}
                onSelectField={setSelectedField}
                fieldSearch={fieldSearch}
                onSearchChange={setFieldSearch}
                onFieldChange={handleFieldChange}
                entity={entity}
              />
            )}

            {activeTab === 'visibility' && (
              <VisibilityTab
                entityFields={entityFields}
                onFieldChange={handleFieldChange}
              />
            )}

            {activeTab === 'relationships' && (
              <RelationshipsTab relationships={entity.relationships || {}} />
            )}

            {activeTab === 'json' && (
              <JsonViewTab entity={entity} />
            )}
          </div>

          {/* Field Editor Sidebar */}
          {activeTab === 'fields' && selectedField && entityFields[selectedField] && (
            <FieldEditorSidebar
              fieldName={selectedField}
              fieldConfig={entityFields[selectedField]}
              activeTab={activeFieldTab}
              onTabChange={setActiveFieldTab}
              onChange={handleFieldChange}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {activeTab === 'fields' && selectedField && (
              <span>Editing: <strong>{selectedField}</strong></span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Field Modal Component
const AddFieldModal = ({ entity, onClose, onSuccess }) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Field configuration
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameTH, setDisplayNameTH] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState('text');
  const [inputType, setInputType] = useState('text');
  const [required, setRequired] = useState(false);
  const [showInList, setShowInList] = useState(true);
  const [showInForm, setShowInForm] = useState(true);
  const [showInDetail, setShowInDetail] = useState(true);
  const [group, setGroup] = useState('General');

  useEffect(() => {
    loadSchema();
  }, []);

  // Auto-update field name and display name when column is selected
  useEffect(() => {
    if (selectedColumn) {
      if (!fieldName) {
        setFieldName(selectedColumn);
      }
      if (!displayName) {
        // Convert column name to display name (e.g., "FirstName" -> "First Name")
        const display = selectedColumn
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
        setDisplayName(display);
        if (!displayNameTH) {
          setDisplayNameTH(display);
        }
      }
    }
  }, [selectedColumn, fieldName, displayName, displayNameTH]);

  // Auto-detect data type and input type from column definition
  useEffect(() => {
    if (selectedTable && selectedColumn && schema?.tables?.[selectedTable]?.columns?.[selectedColumn]) {
      const columnDef = schema.tables[selectedTable].columns[selectedColumn].toUpperCase();
      
      // Infer data type
      if (columnDef.includes('INT') || columnDef.includes('BIGINT') || columnDef.includes('SMALLINT')) {
        setDataType('integer');
        setInputType('number');
      } else if (columnDef.includes('DECIMAL') || columnDef.includes('NUMERIC') || columnDef.includes('MONEY')) {
        setDataType('decimal');
        setInputType('number');
      } else if (columnDef.includes('BIT')) {
        setDataType('boolean');
        setInputType('checkbox');
      } else if (columnDef === 'DATE') {
        setDataType('date');
        setInputType('date');
      } else if (columnDef.includes('DATETIME') || columnDef.includes('TIMESTAMP')) {
        setDataType('datetime');
        setInputType('datetime');
      } else if (columnDef.includes('NVARCHAR(MAX)') || columnDef.includes('TEXT')) {
        setDataType('textarea');
        setInputType('textarea');
      } else {
        setDataType('text');
        setInputType('text');
      }

      // Check if required
      setRequired(columnDef.includes('NOT NULL') && !columnDef.includes('PRIMARY KEY') && !columnDef.includes('IDENTITY'));
    }
  }, [selectedTable, selectedColumn, schema]);

  useEffect(() => {
    if (displayName && !displayNameTH) {
      setDisplayNameTH(displayName);
    }
  }, [displayName, displayNameTH]);

  const loadSchema = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/schema', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load schema');
      }

      const data = await response.json();
      const loadedSchema = data?.data?.schema || data?.data || data;
      setSchema(loadedSchema);
      
      // Auto-select primary table if available
      if (entity?.tables?.primary && loadedSchema?.tables?.[entity.tables.primary]) {
        setSelectedTable(entity.tables.primary);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!selectedTable || !selectedColumn || !fieldName) {
        throw new Error('Please select a table and column, and provide a field name');
      }

      // Check if field already exists
      if (entity.fields && entity.fields[fieldName]) {
        throw new Error(`Field "${fieldName}" already exists in this entity`);
      }

      // Get column definition from schema
      const columnDefinition = schema.tables[selectedTable].columns[selectedColumn];

      // Create field configuration
      const newField = {
        displayName: displayName || fieldName,
        displayNameTH: displayNameTH || displayName || fieldName,
        description: description || `${displayName || fieldName} field`,
        dataType: dataType,
        database: {
          table: selectedTable,
          column: selectedColumn,
          type: columnDefinition
        },
        validation: {
          required: required
        },
        ui: {
          inputType: inputType,
          showInList: showInList,
          showInDetail: showInDetail,
          showInForm: showInForm,
          showInExport: true,
          order: Object.keys(entity.fields || {}).length + 1
        },
        group: group || 'General'
      };

      // Add field to entity configuration
      const updatedEntity = {
        ...entity,
        fields: {
          ...entity.fields,
          [fieldName]: newField
        }
      };

      // Update entity version
      if (updatedEntity.entity.version) {
        const versionParts = updatedEntity.entity.version.split('.');
        versionParts[2] = (parseInt(versionParts[2] || 0) + 1).toString();
        updatedEntity.entity.version = versionParts.join('.');
      }
      updatedEntity.entity.lastUpdated = new Date().toISOString().split('T')[0];

      // Save to backend
      const response = await fetch(`/api/entity-config/${entity.entity.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ configuration: updatedEntity })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to add field');
      }

      // Success message is handled in onSuccess callback
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const availableTables = schema?.tables ? Object.keys(schema.tables) : [];
  const availableColumns = selectedTable && schema?.tables?.[selectedTable]?.columns 
    ? Object.keys(schema.tables[selectedTable].columns) 
    : [];

  // Get existing field columns to filter them out
  const existingColumns = entity?.fields 
    ? Object.values(entity.fields).map(f => f.database?.column).filter(Boolean)
    : [];

  const filteredColumns = availableColumns.filter(col => !existingColumns.includes(col));

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading schema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>Add Field to {entity.entity.displayName}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {/* Database Mapping Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center space-x-2">
              <Database className="h-4 w-4" />
              <span>Database Mapping</span>
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Table <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedTable}
                  onChange={(e) => {
                    setSelectedTable(e.target.value);
                    setSelectedColumn(''); // Reset column when table changes
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select a table...</option>
                  {availableTables.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
                {selectedTable && schema?.tables?.[selectedTable]?.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {schema.tables[selectedTable].description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Column <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedColumn}
                  onChange={(e) => setSelectedColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  disabled={!selectedTable}
                >
                  <option value="">Select a column...</option>
                  {filteredColumns.map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
                {selectedColumn && schema?.tables?.[selectedTable]?.columns?.[selectedColumn] && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                    {schema.tables[selectedTable].columns[selectedColumn]}
                  </p>
                )}
                {filteredColumns.length === 0 && selectedTable && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    All columns from this table are already mapped
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Field Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Field Information</span>
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., FirstName"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Internal field name (alphanumeric, no spaces)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., First Name"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name (Thai)
                </label>
                <input
                  type="text"
                  value={displayNameTH}
                  onChange={(e) => setDisplayNameTH(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="เช่น ชื่อ"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  ชื่อภาษาไทยสำหรับแสดงใน UI
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="2"
                placeholder="Field description"
              />
            </div>
          </div>

          {/* Field Configuration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Field Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Type
                </label>
                <select
                  value={dataType}
                  onChange={(e) => {
                    setDataType(e.target.value);
                    // Auto-update input type based on data type
                    if (e.target.value === 'boolean') setInputType('checkbox');
                    else if (e.target.value === 'date') setInputType('date');
                    else if (e.target.value === 'datetime') setInputType('datetime');
                    else if (e.target.value === 'integer' || e.target.value === 'decimal') setInputType('number');
                    else if (e.target.value === 'textarea') setInputType('textarea');
                    else setInputType('text');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="integer">Integer</option>
                  <option value="decimal">Decimal</option>
                  <option value="boolean">Boolean</option>
                  <option value="date">Date</option>
                  <option value="datetime">DateTime</option>
                  <option value="textarea">Textarea</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Input Type
                </label>
                <select
                  value={inputType}
                  onChange={(e) => setInputType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="date">Date</option>
                  <option value="datetime">DateTime</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Select</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Group
              </label>
              <input
                type="text"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Basic Information"
              />
            </div>
          </div>

          {/* UI Visibility */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">UI Visibility</h4>
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showInList}
                  onChange={(e) => setShowInList(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show in List</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showInForm}
                  onChange={(e) => setShowInForm(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show in Form</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showInDetail}
                  onChange={(e) => setShowInDetail(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show in Detail</span>
              </label>
            </div>
          </div>

          {/* Validation */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Validation</h4>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
            </label>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !selectedTable || !selectedColumn || !fieldName}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  <span>Add Field</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FieldNameModal = ({ entity, onClose, onSuccess }) => {
  const [fields, setFields] = useState([]);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entity) return;

    const mergedFields = entity.config?.fields || {};
    const overrides = entity.overrides?.fieldModifications || {};
    const globalFields = entity.globalConfig?.fields || {};

    const entries = Object.entries(mergedFields).map(([name, fieldConfig]) => {
      const baseField = globalFields[name] || {};
      const override = overrides[name] || {};

      const baseDisplayName = baseField.displayName || name;
      const baseDisplayNameTH = baseField.displayNameTH || baseDisplayName;

      const currentDisplayName = fieldConfig.displayName || baseDisplayName;
      const currentDisplayNameTH = fieldConfig.displayNameTH || baseDisplayNameTH;

      const overrideDisplayName =
        override.displayName ?? currentDisplayName;
      const overrideDisplayNameTH =
        override.displayNameTH ?? currentDisplayNameTH;

      const groupName =
        fieldConfig.group ||
        fieldConfig.ui?.group ||
        'General';

      return {
        name,
        displayName: overrideDisplayName,
        displayNameTH: overrideDisplayNameTH,
        originalDisplayName: overrideDisplayName,
        originalDisplayNameTH: overrideDisplayNameTH,
        baseDisplayName,
        baseDisplayNameTH,
        group: groupName
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    setFields(entries);
  }, [entity]);

  const handleFieldChange = (index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [key]: value
      };
      return next;
    });
  };

  const handleResetField = (index) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        displayName: next[index].baseDisplayName,
        displayNameTH: next[index].baseDisplayNameTH
      };
      return next;
    });
  };

  const hasChanges = fields.some(
    (field) =>
      field.displayName !== field.originalDisplayName ||
      field.displayNameTH !== field.originalDisplayNameTH
  );

  const filteredFields = fields.filter((field) => {
    const query = filter.trim().toLowerCase();
    if (!query) return true;
    return (
      field.name.toLowerCase().includes(query) ||
      (field.displayName || '').toLowerCase().includes(query) ||
      (field.displayNameTH || '').toLowerCase().includes(query) ||
      (field.group || '').toLowerCase().includes(query)
    );
  });

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Super Admin: Update global configuration directly
      const globalConfig = JSON.parse(JSON.stringify(entity.globalConfig));

      // Update field display names in global config
      fields.forEach((field) => {
        if (globalConfig.fields && globalConfig.fields[field.name]) {
          globalConfig.fields[field.name].displayName = field.displayName?.trim() || field.name;
          globalConfig.fields[field.name].displayNameTH = field.displayNameTH?.trim() || field.displayName?.trim() || field.name;
        }
      });

      // Update entity version
      if (globalConfig.entity && globalConfig.entity.version) {
        const versionParts = globalConfig.entity.version.split('.');
        versionParts[2] = (parseInt(versionParts[2] || 0) + 1).toString();
        globalConfig.entity.version = versionParts.join('.');
      }
      if (globalConfig.entity) {
        globalConfig.entity.lastUpdated = new Date().toISOString().split('T')[0];
      }

      // Save to global entity config
      const response = await fetch(
        `/api/entity-config/${entity.entityName}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
          },
          body: JSON.stringify({ configuration: globalConfig })
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update field display names');
      }

      alert('✅ Global field display names updated successfully!');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <Type className="h-5 w-5" />
              <span>
                Edit Global Field Display Names:{' '}
                {entity?.config?.entity?.displayName || entity.entityName}
              </span>
            </h3>
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center space-x-1">
              <AlertTriangle className="h-3 w-3" />
              <span>Changes will affect all tenants globally</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search Fields
              </label>
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Search by field name, display name, or group..."
              />
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredFields.length} of {fields.length} fields
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {filteredFields.map((field, index) => {
            const changed =
              field.displayName !== field.originalDisplayName ||
              field.displayNameTH !== field.originalDisplayNameTH;

            return (
              <div
                key={field.name}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900 space-y-4"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {field.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Group: {field.group || 'General'}
                    </p>
                  </div>
                  {changed && (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                      Modified
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Name (English)
                    </label>
                    <input
                      type="text"
                      value={field.displayName}
                      onChange={(e) =>
                        handleFieldChange(index, 'displayName', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Name (Thai)
                    </label>
                    <input
                      type="text"
                      value={field.displayNameTH}
                      onChange={(e) =>
                        handleFieldChange(index, 'displayNameTH', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {changed && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleResetField(index)}
                      className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                    >
                      Reset to original
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {filteredFields.length === 0 && (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
              No fields match your search criteria.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Validation Modal Component
const ValidationModal = ({ entityName, validation, onClose, onFieldsRemoved }) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);

  const handleToggleField = (fieldName) => {
    setSelectedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleSelectAll = () => {
    if (selectedFields.length === validation.invalidFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...validation.invalidFields]);
    }
  };

  const handleRemoveFields = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to remove');
      return;
    }

    const confirmMessage = `Are you sure you want to remove ${selectedFields.length} field(s) from ${entityName}?\n\n` +
      `Fields to be removed:\n${selectedFields.join(', ')}\n\n` +
      `This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRemoving(true);
    setError(null);

    try {
      const response = await fetch(`/api/tenant-entity-config/${entityName}/remove-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ fields: selectedFields })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove fields');
      }

      const result = await response.json();
      alert(`✅ Successfully removed ${result.result.totalRemoved} field(s) from ${entityName}`);
      onFieldsRemoved();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(false);
    }
  };

  const allIssues = [
    ...validation.missingColumns,
    ...validation.missingTables
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              {validation.isValid ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span>Validation Passed: {entityName}</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <span>Validation Issues Found: {entityName}</span>
                </>
              )}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {validation.fieldsChecked} fields checked • {validation.invalidFields.length} invalid fields found
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {validation.isValid ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
              <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                All Fields Valid!
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                All {validation.fieldsChecked} fields in this entity are properly mapped to existing database tables and columns.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  Validation Summary
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-orange-700 dark:text-orange-300">Missing Columns:</span>
                    <span className="ml-2 font-semibold text-orange-900 dark:text-orange-100">
                      {validation.missingColumns.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-orange-700 dark:text-orange-300">Missing Tables:</span>
                    <span className="ml-2 font-semibold text-orange-900 dark:text-orange-100">
                      {validation.missingTables.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Select All */}
              {validation.invalidFields.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFields.length === validation.invalidFields.length && validation.invalidFields.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Select All Invalid Fields ({validation.invalidFields.length})
                    </span>
                  </label>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedFields.length} selected
                  </span>
                </div>
              )}

              {/* Issues List */}
              <div className="space-y-3">
                {allIssues.map((issue, index) => (
                  <div
                    key={`${issue.field}-${index}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(issue.field)}
                          onChange={() => handleToggleField(issue.field)}
                          className="mt-1 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {issue.field}
                            </span>
                            {issue.displayName && issue.displayName !== issue.field && (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                ({issue.displayName})
                              </span>
                            )}
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                              <Table className="h-3 w-3" />
                              <span>Table: <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{issue.table}</code></span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                              <Columns className="h-3 w-3" />
                              <span>Column: <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{issue.column}</code></span>
                            </div>
                            {issue.group && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Group: {issue.group}
                              </div>
                            )}
                          </div>
                          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                            {validation.missingTables.some(t => t.field === issue.field)
                              ? `⚠️ Table "${issue.table}" does not exist in schema`
                              : `⚠️ Column "${issue.column}" does not exist in table "${issue.table}"`
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {!validation.isValid && selectedFields.length > 0 && (
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                ⚠️ {selectedFields.length} field(s) selected for removal
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Close
            </button>
            {!validation.isValid && selectedFields.length > 0 && (
              <button
                onClick={handleRemoveFields}
                disabled={removing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {removing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Remove Selected Fields ({selectedFields.length})</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityConfigManager;

